import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import {
  Banknote, ShieldCheck, AlertTriangle, CheckCircle2, Loader2, Link2, Trash2, Plus, RefreshCw,
} from "lucide-react";
import { useFeatureMode, isFinancialExecutionAllowed } from "../hooks/useFeatureMode";
import SafetyFreezeNotice from "./SafetyFreezeNotice";

function CredStatusBadge({ status }) {
  if (!status?.configured) {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1" data-testid="cpd-badge-none">
        <AlertTriangle className="h-3 w-3" /> No destination
      </Badge>
    );
  }
  if (status.status === "active") {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1" data-testid="cpd-badge-active">
        <CheckCircle2 className="h-3 w-3" /> Active
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 gap-1" data-testid="cpd-badge-other">
      {status.status || "configured"}
    </Badge>
  );
}

export default function CharityPaymentDestinations() {
  const featureMode = useFeatureMode();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [dialogOrg, setDialogOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  // form state
  const [acctId, setAcctId] = useState("");
  const [routingModel, setRoutingModel] = useState("charity_funded");
  const [charityBps, setCharityBps] = useState(10000);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/platform/charity-payment-destinations");
      setRows(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payment destinations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openConfigure = (row) => {
    setDialogOrg(row);
    setAcctId(row?.credentialStatus?.stripeAccountId || "");
    setRoutingModel(row?.credentialStatus?.routingModel || "charity_funded");
    setCharityBps(10000);
  };

  const saveDestination = async () => {
    if (!dialogOrg) return;
    if (!acctId.trim().startsWith("acct_")) {
      toast.error("Enter a valid Stripe Connect account id (acct_…)");
      return;
    }
    setSaving(true);
    try {
      const platformBps = 10000 - Number(charityBps || 0);
      const payload = {
        organisationId: dialogOrg.organisationId,
        credentialType: "connect",
        stripeAccountId: acctId.trim(),
        routingModel,
        feeAllocation: { platformBps, agencyBps: 0, charityBps: Number(charityBps || 0) },
      };
      await apiClient.post(`/oci/charity/${dialogOrg.organisationId}/payment-credentials`, payload);
      toast.success("Payment destination configured");
      setDialogOrg(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save destination");
    } finally {
      setSaving(false);
    }
  };

  const revokeDestination = async (row) => {
    if (!window.confirm(`Revoke the Stripe destination for ${row.name}? Donations will stop routing to this charity until reconfigured.`)) return;
    try {
      await apiClient.delete(`/oci/charity/${row.organisationId}/payment-credentials`);
      toast.success("Destination revoked");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const startConnectOnboarding = async (row) => {
    try {
      const res = await apiClient.post(`/oci/charity/${row.organisationId}/connect/start`, {
        origin_url: window.location.origin,
      });
      if (res.data?.onboardingUrl) {
        window.open(res.data.onboardingUrl, "_blank", "noopener");
        toast.success("Stripe Connect onboarding opened in a new tab");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Stripe Connect onboarding");
    }
  };

  // Package 0 Safety Freeze — hide payment-destination controls in statements-only mode.
  if (!isFinancialExecutionAllowed(featureMode)) {
    return (
      <div data-testid="charity-payment-destinations-disabled" className="space-y-4">
        <SafetyFreezeNotice
          title="Charity payment destinations are not part of the current release"
          message="Payment destination configuration is not part of the current release."
          testId="charity-payment-destinations-safety-notice"
        />
      </div>
    );
  }

  return (
    <div data-testid="charity-payment-destinations" className="space-y-4">
      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Banknote className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-slate-900">Charity Payment Destinations</h2>
                <p className="text-sm text-slate-500">
                  Master record of where donor funds settle. Charity owns its Stripe Connect destination — campaigns route here by beneficiary organisation.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={load} data-testid="cpd-refresh-btn" className="gap-1">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <ShieldCheck className="h-4 w-4 mt-0.5 text-indigo-500 shrink-0" />
            <span>
              Ownership separation — <span className="font-medium">Platform</span>: billing only ·{" "}
              <span className="font-medium">Agency</span>: campaign operations ·{" "}
              <span className="font-medium">Charity</span>: donor + payment + Stripe ownership.
              Stripe credentials are never campaign-owned and never duplicated across campaigns.
            </span>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm mt-6" data-testid="cpd-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading destinations…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 mt-6" data-testid="cpd-empty">No charity-type organisations found.</p>
          ) : (
            <div className="mt-5 space-y-3" data-testid="cpd-list">
              {rows.map((row) => {
                const s = row.credentialStatus || {};
                return (
                  <div
                    key={row.organisationId}
                    className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                    data-testid={`cpd-row-${row.organisationId}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">{row.name}</span>
                        <CredStatusBadge status={s} />
                      </div>
                      <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                        <div>Owner: <span className="font-mono">{row.ownerEmail || "—"}</span></div>
                        {s.configured && s.stripeAccountId && (
                          <div className="flex items-center gap-1">
                            <Link2 className="h-3 w-3" /> <span className="font-mono">{s.stripeAccountId}</span>
                            {s.routingModel && <span className="ml-1">· {s.routingModel}</span>}
                          </div>
                        )}
                        <div>Linked campaigns: <span className="font-medium">{row.linkedCampaignCount ?? 0}</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => startConnectOnboarding(row)}
                        data-testid={`cpd-connect-${row.organisationId}`}
                        className="gap-1"
                      >
                        <Link2 className="h-3.5 w-3.5" /> Stripe Connect
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => openConfigure(row)}
                        data-testid={`cpd-configure-${row.organisationId}`}
                        className="gap-1"
                      >
                        <Plus className="h-3.5 w-3.5" /> {s.configured ? "Reconfigure" : "Configure destination"}
                      </Button>
                      {s.configured && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => revokeDestination(row)}
                          data-testid={`cpd-revoke-${row.organisationId}`}
                          className="text-rose-600 hover:bg-rose-50 gap-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Revoke
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!dialogOrg} onOpenChange={(v) => { if (!v) setDialogOrg(null); }}>
        <DialogContent data-testid="cpd-configure-dialog">
          <DialogHeader>
            <DialogTitle>Configure Stripe Connect destination</DialogTitle>
            <DialogDescription>
              {dialogOrg?.name} — donor funds for campaigns routed to this charity will settle to this Connect account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-600">Stripe Connect Account ID</Label>
              <Input
                placeholder="acct_…"
                value={acctId}
                onChange={(e) => setAcctId(e.target.value)}
                data-testid="cpd-acct-input"
                className="mt-1 font-mono text-sm"
              />
              <p className="text-[11px] text-slate-400 mt-1">Public identifier only (no secret keys). The charity owns this account.</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Routing Model</Label>
              <Select value={routingModel} onValueChange={setRoutingModel}>
                <SelectTrigger className="mt-1" data-testid="cpd-routing-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="charity_funded">Charity funded (charity receives 100%)</SelectItem>
                  <SelectItem value="agency_funded">Agency funded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Charity share (bps, of 10000)</Label>
              <Input
                type="number"
                value={charityBps}
                onChange={(e) => setCharityBps(e.target.value)}
                data-testid="cpd-charitybps-input"
                className="mt-1"
              />
              <p className="text-[11px] text-slate-400 mt-1">Remainder goes to platform application fee. 10000 = 100% to charity.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOrg(null)} data-testid="cpd-cancel-btn">Cancel</Button>
            <Button onClick={saveDestination} disabled={saving} data-testid="cpd-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save destination"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
